import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Flame, FileText, FileDown, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS = { nuova: "bg-amber-500", in_lavorazione: "bg-blue-600", offerta_inviata: "bg-purple-600", attivata: "bg-teal-600", persa: "bg-red-600", autorizzata: "bg-teal-600" };
const STATUS_LABEL = { nuova: "Nuova", in_lavorazione: "In lavorazione", offerta_inviata: "Offerta inviata", attivata: "Attivata", persa: "Persa", autorizzata: "Autorizzata" };
const CONSENT = { in_attesa: { label: "Consenso in attesa", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" }, autorizzata: { label: "Consenso ottenuto", cls: "bg-teal-500/15 text-teal-500 border-teal-500/30" } };
const TIPO = { luce: "Luce", gas: "Gas", dual: "Luce + Gas" };

export default function AdminEnergyRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const { user } = useAuth();

  const remove = async () => {
    if (!window.confirm("Eliminare questa richiesta? Andrà nel Cestino (ripristinabile entro 60 giorni).")) return;
    try { await api.delete(`/energy/requests/${sel.id}`); toast.success("Richiesta eliminata"); setSel(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  const load = () => {
    setLoading(true); setLoadError("");
    return api.get("/energy/requests").then((r) => setRows(r.data || []))
      .catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Storico richieste non disponibile"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const open = (r) => {
    setSel(r);
    setEdit({ status: r.status || "nuova", offerta_brand: r.offerta_brand || "", prezzo_offerta: r.prezzo_offerta ?? "", risparmio_stimato: r.risparmio_stimato ?? "", note_admin: r.note_admin || "" });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/energy/requests/${sel.id}`, {
        status: edit.status, offerta_brand: edit.offerta_brand,
        prezzo_offerta: edit.prezzo_offerta === "" ? null : parseFloat(edit.prezzo_offerta),
        risparmio_stimato: edit.risparmio_stimato === "" ? null : parseFloat(edit.risparmio_stimato),
        note_admin: edit.note_admin,
      });
      toast.success("Richiesta aggiornata"); setSel(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-energy-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Gestione</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Flame className="w-7 h-7 text-primary" /> Storico Energia &amp; Gas — modulo precedente</h1>
        <p className="text-sm text-muted-foreground mt-2" data-testid="legacy-energy-history-notice">Il vecchio modulo è disattivato per le nuove richieste. Le pratiche già registrate restano disponibili qui.</p>
      </div>

      {loadError && <div role="alert" className="flex items-center gap-3 text-sm text-red-500" data-testid="legacy-energy-history-error">{loadError}<Button variant="outline" size="sm" onClick={load} data-testid="legacy-energy-history-retry">Riprova</Button></div>}
      <div className="grid gap-3">
        {loading && <p className="text-muted-foreground" data-testid="legacy-energy-history-loading">Caricamento dello storico…</p>}
        {rows.map((r) => (
          <Card key={r.id} className="p-4 glow-border flex items-center justify-between gap-3 cursor-pointer hover:border-primary/50" onClick={() => open(r)} data-testid={`energy-row-${r.id}`}>
            <div className="min-w-0">
              <h3 className="font-head font-semibold truncate">{r.nome} {r.cognome} · <span className="text-muted-foreground">{TIPO[r.tipo_fornitura]}</span></h3>
              <p className="text-sm text-muted-foreground truncate">{r.fornitore_attuale || "—"} · spesa {r.spesa_attuale != null ? `${euro(r.spesa_attuale)}/${r.periodicita_spesa}` : "—"} · dealer <EntityLink id={r.dealer_id} data-testid={`legacy-dealer-link-${r.id}`}>{r.dealer_name}</EntityLink> · {(r.created_at || "").slice(0, 10)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {r.has_bill && <Badge variant="outline" className="gap-1"><FileText className="w-3 h-3" /> Luce</Badge>}
              {r.has_bill_gas && <Badge variant="outline" className="gap-1"><FileText className="w-3 h-3" /> Gas</Badge>}
              {r.consent_status && <Badge variant="outline" className={CONSENT[r.consent_status]?.cls} data-testid={`energy-consent-${r.id}`}>{CONSENT[r.consent_status]?.label || r.consent_status}</Badge>}
              <Badge className={STATUS[r.status] || "bg-muted"}>{STATUS_LABEL[r.status] || r.status}</Badge>
            </div>
          </Card>
        ))}
        {!loading && !loadError && rows.length === 0 && <p className="text-muted-foreground" data-testid="legacy-energy-history-empty">Nessuna richiesta ricevuta.</p>}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Richiesta — {sel?.nome} {sel?.cognome}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Info label="Tipo" value={TIPO[sel.tipo_fornitura]} />
                <Info label="Fornitore attuale" value={sel.fornitore_attuale} />
                <Info label="Cod. fiscale" value={sel.codice_fiscale} />
                <Info label="Email" value={sel.email} />
                <Info label="Telefono" value={sel.telefono} />
                <Info label="Consumo annuo" value={sel.consumo_annuo} />
                <Info label="POD" value={sel.pod} />
                <Info label="PDR" value={sel.pdr} />
                <Info label="Indirizzo" value={`${sel.indirizzo_fornitura || ""} ${sel.cap || ""} ${sel.citta || ""} ${sel.provincia || ""}`} />
                <Info label="Spesa attuale" value={sel.spesa_attuale != null ? `${euro(sel.spesa_attuale)} / ${sel.periodicita_spesa}` : "—"} />
              </div>
              {sel.note && <Info label="Note dealer" value={sel.note} />}
              {sel.has_bill && (
                <a href={`${backend}/api/energy/requests/${sel.id}/bill`} target="_blank" rel="noopener noreferrer" data-testid="energy-bill-link">
                  <Button variant="outline" size="sm" className="gap-2"><FileText className="w-4 h-4" /> Apri bolletta</Button>
                </a>
              )}
              {sel.consent_status === "autorizzata" && (
                <a href={`${backend}/api/energy/requests/${sel.id}/consent-receipt`} target="_blank" rel="noopener noreferrer" data-testid="energy-consent-receipt-link">
                  <Button variant="outline" size="sm" className="gap-2"><FileDown className="w-4 h-4" /> Ricevuta consenso</Button>
                </a>
              )}
              {sel.consent_status !== "autorizzata" && (
                <Button variant="outline" size="sm" className="gap-2 text-red-500" onClick={remove} data-testid="energy-delete-btn"><Trash2 className="w-4 h-4" /> Elimina richiesta</Button>
              )}

              {user?.role === "admin" && <div className="border-t border-border pt-3 space-y-3">
                <p className="font-medium">Gestione & Offerta brand</p>
                <div>
                  <Label>Stato</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="energy-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Brand / offerta proposta</Label><Input className="mt-1.5" data-testid="energy-brand-input" value={edit.offerta_brand} onChange={(e) => setEdit({ ...edit, offerta_brand: e.target.value })} placeholder="es. Brand X — Tariffa Fissa Luce" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Prezzo offerta (€)</Label><Input type="number" step="0.01" className="mt-1.5" data-testid="energy-price-input" value={edit.prezzo_offerta} onChange={(e) => setEdit({ ...edit, prezzo_offerta: e.target.value })} /></div>
                  <div><Label>Risparmio stimato (€)</Label><Input type="number" step="0.01" className="mt-1.5" data-testid="energy-saving-input" value={edit.risparmio_stimato} onChange={(e) => setEdit({ ...edit, risparmio_stimato: e.target.value })} /></div>
                </div>
                <div><Label>Note interne</Label><Input className="mt-1.5" data-testid="energy-adminnote-input" value={edit.note_admin} onChange={(e) => setEdit({ ...edit, note_admin: e.target.value })} /></div>
                <Button onClick={save} disabled={busy} className="w-full" data-testid="energy-save-btn">{busy ? "Salvataggio…" : "Salva"}</Button>
              </div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium break-words">{label === "Telefono" ? <PhoneDisplay value={value} data-testid="legacy-energy-phone" /> : value || "—"}</p></div>
);
